import { TIER_STAT_MULT, archetypeById, unitById, zombieById, type AbilityRef, type PackTemplate, type UnitDef, type ZombieDef } from "@zend/content";
import { ROLE_BASE, levelMultiplier, unitStats, type StatBlock, type StatMods } from "../units/stats.js";
import { abilityMagnitude } from "../units/progression.js";
import type { CombatSlot, CombatantInput, CombatantMeta, ResolvedAbility, SideInput, SlotKind } from "./types.js";

export function slotKind(slot: CombatSlot): SlotKind {
  if (slot === "commander") return "commander";
  if (slot === "s1" || slot === "s2") return "support";
  if (slot === "turret") return "turret";
  return "fighter";
}

/** A player unit as a battle input; stats come from the shared pipeline (level, stars, chemistry, modules…). */
export function combatantFromUnit(id: string, def: UnitDef, mods: StatMods, abilityLevels: number[], slot: CombatSlot, hpPct = 100): CombatantInput {
  const stats = unitStats(def, mods);
  const abilities = [def.standard, ...def.uniques];
  const skinHp = typeof mods.skinPct === "number" ? mods.skinPct : (mods.skinPct.hp ?? 0);
  return { id, kind: "unit", defId: def.id, slot, level: mods.level, stars: mods.stars, abilityLevels: abilities.map((_, i) => abilityLevels[i] ?? 1), stats, hpPct, ...(def.role === "commander" && skinHp ? { commanderHpPct: skinHp } : {}) };
}

/** Zombie stats: role base × tier × level × archetype multipliers (40-combat.md R34). */
export function zombieStats(def: ZombieDef, level: number): StatBlock {
  const base = ROLE_BASE[def.roleAnalogue];
  const scale = TIER_STAT_MULT[def.tier] * levelMultiplier(level);
  return {
    ...base,
    hp: Math.round(base.hp * scale * def.statMult.hp),
    atk: Math.round(base.atk * scale * def.statMult.atk),
    def: Math.round(base.def * scale * def.statMult.def),
    intervalTicks: Math.max(3, Math.round(base.intervalTicks * def.statMult.speed)),
  };
}

export function combatantFromZombie(id: string, def: ZombieDef, level: number, slot: CombatSlot, hpPct = 100): CombatantInput {
  const abilityLevel = Math.max(1, Math.min(10, Math.ceil(level / 12)));
  return { id, kind: "zombie", defId: def.id, slot, level, stars: 0, abilityLevels: def.abilities.map(() => abilityLevel), stats: zombieStats(def, level), hpPct };
}

export function turretCombatant(id: string, hp: number, atk: number): CombatantInput {
  const stats: StatBlock = { hp, atk, def: 0, intervalTicks: 15, crit: 0, critDmg: 1.5, acc: 100, eva: 0, energyPerAttack: 0, cargo: 0 };
  return { id, kind: "turret", defId: "turret", slot: "turret", level: 1, stars: 0, abilityLevels: [1], stats, hpPct: 100 };
}

const TURRET_ABILITY: AbilityRef = { archetype: "abl.single_strike", name: { de: "Geschützfeuer", en: "Turret Fire" }, params: { mult: 1 } };

export function abilityRefs(input: CombatantInput): AbilityRef[] {
  if (input.kind === "turret") return [TURRET_ABILITY];
  if (input.kind === "zombie") return zombieById(input.defId).abilities;
  const def = unitById(input.defId);
  return [def.standard, ...def.uniques];
}

export function resolveAbilities(input: CombatantInput): ResolvedAbility[] {
  return abilityRefs(input).map((ref, index) => {
    const arch = archetypeById(ref.archetype);
    const params: Record<string, number> = {};
    for (const [k, range] of Object.entries(arch.params)) params[k] = ref.params[k] ?? range.default;
    const level = input.abilityLevels[index] ?? 1;
    return { index, archetype: ref.archetype, kind: arch.kind, targets: arch.targets, params, level, magnitude: abilityMagnitude(level), usedOnce: false, readyAtTick: 0 };
  });
}

export function combatantMeta(input: CombatantInput): CombatantMeta {
  if (input.kind === "turret") return { role: "defender", chassis: null, tier: "D" };
  if (input.kind === "zombie") {
    const z = zombieById(input.defId);
    return { role: z.roleAnalogue, chassis: null, tier: z.tier };
  }
  const u = unitById(input.defId);
  return { role: u.role, chassis: u.chassis, tier: u.tier };
}

/** A zombie pack template as a battle side: commander, up to four fighters, up to two supports (R34). */
export function sideFromPack(pack: PackTemplate, idPrefix = "z"): SideInput {
  const members: CombatantInput[] = [];
  let fighters = 0;
  let supports = 0;
  pack.members.forEach((m, i) => {
    let slot: CombatSlot;
    if (m.slot === "commander") slot = "commander";
    else if (m.slot === "support") {
      supports++;
      if (supports > 2) return;
      slot = supports === 1 ? "s1" : "s2";
    } else {
      fighters++;
      if (fighters > 4) return;
      slot = (["f1", "f2", "f3", "f4"] as const)[fighters - 1]!;
    }
    members.push(combatantFromZombie(`${idPrefix}${i + 1}`, zombieById(m.zombieId), m.level, slot));
  });
  return { members };
}
